import { api } from '@/utils/api';
import { InvestigationCommandType } from '@/types/incidentInvestigation';

export type QueueStatus = 'PENDING' | 'REJECTED';
export interface QueueEntry {
  eventId: string;
  type: string;
  payload: any;
  capturedAt: string;
  status?: QueueStatus;
  reason?: string;
  attempts?: number;
  nextAttemptAt?: number;
}

// Legacy v1 entries have no provable owner: retain them, but never automatically replay them.
export function queueStorageKey(): string | null {
  try {
    const user = JSON.parse(localStorage.getItem('user') || 'null');
    if (!localStorage.getItem('token') || !Number.isSafeInteger(user?.id) || !Number.isSafeInteger(user?.companyId)) return null;
    return 'conformara.initial-report.queue.v2.' + user.companyId + '.' + user.id;
  } catch { return null; }
}
const listeners = new Set<() => void>();
let replaying = false;

// ─── Account scope (Rule S, identity-seam design §4) ─────────────────────────
// Every user-triggered operation captures ONE account scope before its first
// `await`, threads that scope through every localStorage read/write it
// performs, and gates its UI updates on the scope still being current.
// `queueStorageKey()` must never be recomputed inside an async continuation.
export interface AccountScope { key: string | null; token: string | null; stillCurrent(): boolean }
export function captureAccountScope(): AccountScope {
  const key = queueStorageKey();
  const token = localStorage.getItem('token');
  return { key, token, stillCurrent: () => queueStorageKey() === key };
}

// Q4 (owner decision): the legacy free-floating pointer
// `<queueKey>.pendingSubmitWithoutId` is DELETED (§6) — it is the account-global
// current-instance selector that caused the cross-instance leaks. It is never
// read; it is deleted opportunistically on any read/write of the seam's stores.
function dropLegacyPointer(key: string | null) {
  if (!key) return;
  try { (localStorage as any).removeItem?.(key + '.pendingSubmitWithoutId'); } catch { /* best-effort */ }
}

function read(key = queueStorageKey()): QueueEntry[] {
  dropLegacyPointer(key);
  if (!key) return [];
  try { return JSON.parse(localStorage.getItem(key) || '[]'); } catch { return []; }
}
function write(entries: QueueEntry[], key = queueStorageKey()) { if (!key) throw new Error('Sign in before saving a report'); dropLegacyPointer(key); localStorage.setItem(key, JSON.stringify(entries)); listeners.forEach((fn) => fn()); }
function eventId(): string { return globalThis.crypto?.randomUUID?.() || `evt-${Date.now()}-${Math.random().toString(36).slice(2)}`; }

export function hasUnownedLegacyQueue() {
  try { return JSON.parse(localStorage.getItem('conformara.initial-report.queue.v1') || '[]').length > 0; } catch { return true; }
}

// recheck Finding 2 (Rule S): pass the CAPTURED scope key across async
// continuations — a default-key read after an await would read whatever account
// is active at that moment.
export function queueEntries(key: string | null = queueStorageKey()) { return read(key); }
export function pendingQueueCount() { return read().filter((entry) => entry.status !== 'REJECTED').length; }
export function rejectedQueueEntries() { return read().filter((entry) => entry.status === 'REJECTED'); }
export function subscribeQueue(listener: () => void) { listeners.add(listener); return () => listeners.delete(listener); }

// ─── Draft record (identity-seam design §2.2) ────────────────────────────────
// ONE record per account holds the CURRENT instance's identity and content:
//   { createEventId, createAttemptedAt?, completedAt?, payload, witness, savedAt }
// `createEventId` is the instance's ONLY identifier (the pre-allocated
// createEventId — also the wire's x-event-id). `createAttemptedAt` marks the
// moment the create actually went on the wire / into the queue (§2.2 "first
// live request / enqueue"), which is what lets §5 C2 tell a pathological
// unbound-after-drain apart from a never-attempted saved draft (whose id must
// never be auto-replayed). `completedAt` is Q3's honest-completion marker.
export interface DraftRecord {
  createEventId?: string;
  createAttemptedAt?: string;
  completedAt?: string;
  payload?: any;
  witness?: any;
  savedAt?: string;
}
const draftStorageKey = (key: string | null = queueStorageKey()) => (key || '') + '.draft';
export function readDraft(key: string | null = queueStorageKey()): DraftRecord | null {
  dropLegacyPointer(key);
  if (!key) return null;
  try { return JSON.parse(localStorage.getItem(draftStorageKey(key)) || 'null'); } catch { return null; }
}
export function writeDraft(record: DraftRecord, key: string | null = queueStorageKey()) {
  if (!key) return;
  dropLegacyPointer(key);
  try { localStorage.setItem(draftStorageKey(key), JSON.stringify(record)); listeners.forEach((fn) => fn()); } catch { /* storage full: identity stays best-effort */ }
}

// UAT-R2-003 follow-up: durable eventId→reportId bindings for replayed/already-
// applied INITIAL_REPORT_CREATE commands. The server's replay response carries
// targetReportId for a CREATE (APPLIED or DUPLICATE); the page reads it here to
// bind its local report id, so it can never re-POST a duplicate create after the
// offline banner clears.
const resolvedCreateKey = (key: string | null = queueStorageKey()) => (key || '') + '.resolvedCreates';
// recheck2 Finding 2: EVERY read/write of the binding map uses ONE explicit
// account scope. Callers that span async boundaries (replayQueue) pass the key
// captured at replay start, so an account/company switch mid-replay can never
// move the write into the new account's storage; only synchronous UI reads
// default to the current user (the active account always reads its OWN map).
function readResolved(key: string | null = queueStorageKey()): Record<string, number> { try { return JSON.parse(localStorage.getItem(resolvedCreateKey(key)) || '{}'); } catch { return {}; } }
export function recordResolvedCreate(eventId: string, reportId: number, key: string | null = queueStorageKey()) {
  if (!key || !Number.isSafeInteger(reportId) || reportId <= 0) return;
  const map = readResolved(key);
  if (map[eventId] === reportId) return;
  try { localStorage.setItem(resolvedCreateKey(key), JSON.stringify({ ...map, [eventId]: reportId })); listeners.forEach((fn) => fn()); } catch { /* storage full: binding stays best-effort */ }
}
export function resolvedCreateReportId(eventId: string, key: string | null = queueStorageKey()): number | null {
  const id = readResolved(key)[eventId];
  return Number.isSafeInteger(id) && id > 0 ? id : null;
}

// ─── Per-instance create intent (identity-seam design §2.3) ──────────────────
// The duplicate-create guard is DERIVED for ONE instance id (the draft record's
// createEventId) — never from an account-global pointer. It fires iff that id
// has a queued CREATE/SUBMIT entry, or an attempted-but-unresolved create
// (the §5 C2 fail-closed case: the guard stays on until the id binds — never a
// silent second create).
export function createIntentUnresolved(createEventId: string | null, key: string | null = queueStorageKey()): boolean {
  if (!createEventId) return false;
  const entries = read(key);
  if (entries.some((entry) => (entry.eventId === createEventId || entry.payload?.createEventId === createEventId) && (entry.type === 'INITIAL_REPORT_CREATE' || entry.type === 'INITIAL_REPORT_SUBMIT'))) return true;
  const draft = readDraft(key);
  return Boolean(draft?.createEventId === createEventId && draft.createAttemptedAt && resolvedCreateReportId(createEventId, key) == null);
}

/** §5 C2: id discovery is idempotent replay, never a second create — re-queue
 * the SAME (eventId, payloadSnapshot) command and replay it; the ledger
 * classifies it DUPLICATE and returns the id. */
export function reconcileUnboundCreate(createEventId: string, payloadSnapshot: any, key: string | null = queueStorageKey()): Promise<void> {
  enqueue('INITIAL_REPORT_CREATE', payloadSnapshot, createEventId, { key });
  return replayQueue();
}

export function enqueue(type: string, payload: any, id = eventId(), opts: { nextAttemptAt?: number; key?: string | null } = {}): QueueEntry {
  const entry: QueueEntry = { eventId: id, type, payload, capturedAt: new Date().toISOString(), status: 'PENDING', attempts: 0, ...(opts.nextAttemptAt ? { nextAttemptAt: opts.nextAttemptAt } : {}) };
  write([...read(opts.key ?? undefined), entry], opts.key ?? undefined);
  // R2-IR-001: an enqueued entry with a future nextAttemptAt (e.g. the page's
  // PENDING_RETRY enqueue) must self-start its automatic retry — arm its timer
  // right here, never waiting for a user action.
  scheduleRetainedRetries();
  return entry;
}

/** R2-IR-001: the bounded backoff formula's step N (single-sourced). */
export function nextRetryDelayMs(attempts: number): number { return Math.min(60_000, 1000 * 2 ** Math.min(attempts, 6)); }

/**
 * R2-IR-001: arm ONE replace-not-stack timer for every retained PENDING entry
 * whose nextAttemptAt is still in the future. Called at module init (restores
 * timers after a reload), after every enqueue, and after every replay
 * completion (including the network-error branch) so a stamped nextAttemptAt
 * ALWAYS comes with a live timer — the UI's "will retry automatically" is
 * backed by a real schedule. Past-due entries are left to replayQueue().
 */
export function scheduleRetainedRetries(now = Date.now()) {
  for (const entry of read()) {
    if (entry.status === 'REJECTED' || !entry.nextAttemptAt) continue;
    if (entry.nextAttemptAt > now) scheduleEntryRetry(entry.eventId, entry.nextAttemptAt - now);
  }
}

/** Investigation commands share this account-scoped local queue and replay contract. */
export function enqueueInvestigationCommand(type: InvestigationCommandType, payload: any, id?: string): QueueEntry {
  return enqueue(type, payload, id);
}

export function discardQueueEntry(id: string) {
  const key = queueStorageKey();
  const entries = read(key);
  const removed = entries.find((entry) => entry.eventId === id);
  write(entries.filter((entry) => entry.eventId !== id), key);
  // R3: Discard removes ONLY the entry and never rotates the editor's instance
  // (the id stays). Dropping a CREATE entry abandons that upload intent so the
  // §5 C2 reconciliation can never resurrect a command the user discarded.
  if (removed?.type === 'INITIAL_REPORT_CREATE') {
    const draft = readDraft(key);
    if (draft?.createEventId === id && draft.createAttemptedAt) {
      const { createAttemptedAt: _dropped, ...rest } = draft;
      writeDraft(rest, key);
    }
  }
}
/**
 * LUNA18-IR-001/002: mint an event id the caller controls — the LIVE request
 * carries it as its idempotency key (x-event-id) and the SAME id is queued on
 * failure, so the server ledger reconciles a lost response instead of creating
 * a duplicate row.
 */
export function nextEventId(): string { return eventId(); }
export function retryQueueEntry(id: string) {
  // DELTA18-M1: retrying a REJECTED INITIAL_REPORT_CREATE must re-bind every
  // queued EVIDENCE_ATTACH whose payload.createEventId points at the OLD eventId
  // to the NEW eventId in the SAME write (the server maps initialReportId 0
  // through createEventId, so a stale id would be rejected forever), and reset
  // dependent evidence that was REJECTED because of the stale binding to
  // PENDING so it retries against the new binding. Only REJECTED entries retry;
  // PENDING entries (and their ids) are untouched.
  const key = queueStorageKey();
  const entries = read(key);
  const target = entries.find((entry) => entry.eventId === id && entry.status === 'REJECTED');
  if (!target) { void replayQueue(); return; }
  const newId = eventId();
  // §2.4 (identity seam): this is the ONLY place an instance's id token
  // rotates. The draft record's createEventId follows the rebind in the SAME
  // synchronous write as the entry/dependent rebinds — instance identity
  // survives; only its id token changes, atomically.
  try {
    const draft = readDraft(key);
    if (draft?.createEventId === id) writeDraft({ ...draft, createEventId: newId }, key);
  } catch { /* best-effort */ }
  write(entries.map((entry) => {
    if (entry.eventId === id) {
      return { ...entry, eventId: newId, status: 'PENDING' as const, reason: undefined, nextAttemptAt: 0 };
    }
    // QWEN-GAP-05: dependent SUBMITs bind through createEventId exactly like
    // evidence (offline id-0 submits resolve id 0 via the ledger) — a retried
    // CREATE must rebind them in the SAME write or the SUBMIT is rejected
    // forever against the rejected old row.
    if ((entry.type === 'EVIDENCE_ATTACH' || entry.type === 'INITIAL_REPORT_SUBMIT') && entry.payload?.createEventId === id) {
      const rebind = { ...entry, payload: { ...entry.payload, createEventId: newId } };
      if (entry.status === 'REJECTED') return { ...rebind, status: 'PENDING' as const, reason: undefined, nextAttemptAt: 0 };
      return rebind;
    }
    return entry;
  }), key);
  void replayQueue();
}

// LUNA-R6-M1: the AUTOMATIC retry for retained PENDING_RETRY entries. One
// timer per entry, scheduled AT RESULT-HANDLING TIME (fake-timer observable —
// never a module-load setInterval, and independent of the 'online' event).
// Hygiene: re-scheduling the same entry replaces its existing timer (never
// stacked), and a timer whose entry is gone / already sent / inside a newer
// backoff window by the time it fires is a harmless no-op — replayQueue()
// re-filters the batch and honors nextAttemptAt.
const retryTimers = new Map<string, ReturnType<typeof setTimeout>>();
function scheduleEntryRetry(eventId: string, delayMs: number) {
  const prior = retryTimers.get(eventId);
  if (prior) clearTimeout(prior);
  const timer = setTimeout(() => {
    retryTimers.delete(eventId);
    void replayQueue();
  }, Math.max(0, delayMs));
  retryTimers.set(eventId, timer);
}

export async function replayQueue(): Promise<void> {
  if (replaying || !navigator.onLine) return;
  const key = queueStorageKey();
  const token = localStorage.getItem('token');
  if (!key || !token) return;
  const now = Date.now();
  // recheck2 Finding 2: every read AND write inside this replay uses the key
  // captured above — never a key recomputed from a user that changed mid-flight.
  const batch = read(key).filter((entry) => entry.status !== 'REJECTED' && (!entry.nextAttemptAt || entry.nextAttemptAt <= now));
  if (!batch.length) return;
  replaying = true;
  try {
    const response = await api.post('/initial-reports/queue/replay', { commands: batch.map(({ status, reason, attempts, nextAttemptAt, ...command }) => command) }, { headers: { Authorization: 'Bearer ' + token } });
    const results: any[] = response.data?.data || [];
    const byId = new Map<string, any>(results.map((result: any) => [result.eventId, result]));
    // UAT-R2-003 follow-up: persist CREATE id bindings BEFORE the queue write —
    // listeners fire on write(), and the page must see the binding immediately.
    for (const result of results) {
      if ((result.status === 'APPLIED' || result.status === 'DUPLICATE') && Number.isSafeInteger(result.targetReportId)) {
        recordResolvedCreate(result.eventId, result.targetReportId, key);
      }
    }
    // Q3 (owner decision): an applied SUBMIT completes the draft's instance —
    // mark the draft record's completedAt under the SAME captured scope so a
    // later restore can say "Submitted — start a New report to write another".
    for (const result of results) {
      const applied = result.status === 'APPLIED' || result.status === 'DUPLICATE' || result.status === 'PENDING_RETRY';
      if (!applied || /previously rejected/i.test(String(result.reason || ''))) continue;
      const entry = batch.find((candidate) => candidate.eventId === result.eventId);
      if (entry?.type !== 'INITIAL_REPORT_SUBMIT') continue;
      const draft = readDraft(key);
      if (!draft?.createEventId || draft.completedAt) continue;
      const boundId = readResolved(key)[draft.createEventId];
      const forInstance = entry.payload?.createEventId === draft.createEventId || (Number.isSafeInteger(boundId) && entry.payload?.id === boundId);
      if (forInstance) writeDraft({ ...draft, completedAt: new Date().toISOString() }, key);
    }
    const current = read(key);
    const updated = current.flatMap((entry) => {
      const result = byId.get(entry.eventId);
      if (!result || result.status === 'APPLIED') return result ? [] : [entry];
      // SOL final audit IR-002: a DUPLICATE reply whose reason indicates the
      // event was PREVIOUSLY REJECTED (the original REJECTED HTTP response was
      // lost; the server's durable ledger row is REJECTED) must quarantine the
      // entry like a rejection — visible in the "Needs attention" tray — never
      // silently delete it like an ordinary already-applied duplicate.
      if (result.status === 'DUPLICATE' && /previously rejected/i.test(String(result.reason || ''))) {
        const attempts = (entry.attempts || 0) + 1;
        return [{ ...entry, status: 'REJECTED' as const, reason: result.reason || 'Previously rejected (see quarantine)', attempts, nextAttemptAt: Date.now() + Math.min(60_000, 1000 * 2 ** Math.min(attempts, 6)) }];
      }
      if (result.status === 'DUPLICATE') return [];
      // LUNA-R5-H1a: 'PENDING_RETRY' means the command applied server-side but
      // its notification is still pending — RETAIN the entry (status stays
      // PENDING) so it retries later instead of being dropped.
      // LUNA-R6-M1: retention alone strands the entry (the only auto-triggers
      // were module init and the browser 'online' event). Stamp bounded retry
      // metadata (same backoff formula as the REJECTED path) AND schedule the
      // automatic retry here, at result-handling time. replayQueue()'s batch
      // filter already honors nextAttemptAt, which bounds storms: nothing
      // fires inside the backoff window even if replayQueue() is invoked
      // manually.
      if (result.status === 'PENDING_RETRY') {
        const attempts = (entry.attempts || 0) + 1;
        const scheduledAt = Date.now();
        const nextAttemptAt = scheduledAt + Math.min(60_000, 1000 * 2 ** Math.min(attempts, 6));
        scheduleEntryRetry(entry.eventId, nextAttemptAt - scheduledAt);
        return [{ ...entry, attempts, nextAttemptAt }];
      }
      const attempts = (entry.attempts || 0) + 1;
      return [{ ...entry, status: 'REJECTED' as const, reason: result.reason || 'Command rejected', attempts, nextAttemptAt: Date.now() + Math.min(60_000, 1000 * 2 ** Math.min(attempts, 6)) }];
    });
    write(updated, key);
  } catch {
    const current = read(key);
    write(current.map((entry) => entry.status === 'PENDING' ? { ...entry, attempts: (entry.attempts || 0) + 1, nextAttemptAt: Date.now() + Math.min(60_000, 1000 * 2 ** Math.min((entry.attempts || 0) + 1, 6)) } : entry), key);
  } finally { replaying = false; scheduleRetainedRetries(); }
}

if (typeof window !== 'undefined') {
  window.addEventListener('online', () => void replayQueue());
  // R2-IR-001: a reload must restore timers for retained future-dated entries —
  // otherwise the promised automatic retry dies with the previous page load.
  scheduleRetainedRetries();
  void replayQueue();
}